import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { MUTATION_KEY as releaseSessionKey } from '../../../api/releaseCheckoutSession';
import { MUTATION_KEY as updateCheckoutItemsKey, useUpdateCheckoutItems } from '../../../api/updateCheckoutItems';
import { CheckoutCTALabel, getCheckoutCTALabel, useCheckoutContext } from '../../../context/new/CheckoutContext';
import { CheckoutGuardOutletContext } from '../../../routes/CheckoutGuard';
import { CTAButton } from '../../buttons';
import { Notice } from '../../notice-new/Notice';
import { mergeAddOnItemsDTO, syncMandatorySectionSelection } from './ecommerceSelectionHelpers';
import { SelectableEcommerceItem } from '../SelectableEcommerceItem';
import UpdateCheckoutItemsInput = TDR.CheckoutSession.UpdateCheckoutItemsInput;
import { analytics } from '../../../helpers/analytics';
import { resolveLocalizedText } from '../../../helpers/resolveLocalizedText';
import { useTranslation } from 'react-i18next';
import { CTA_CLICKED_EVENT, ECOMMERCE_SECTION_VIEWED } from '../../../helpers/analytics/constants';
import { useGTMAnalytics } from '../../../helpers/gtm/surface/useGTMAnalytics';

export type EcommerceMandatoryItemDTO = {
	id: string;
	quantity: number;
	variantId: string;
};

type MandatorySelectionState = 'selection_required' | 'proceed';

interface Props {
	sectionData: TDR.Ecommerce.EcommerceSection;
}

export const EcommerceMandatorySection = ({ sectionData }: Props) => {
	const { t, i18n } = useTranslation();
	const { navConfig, updateCheckoutState, checkoutState } = useCheckoutContext();
	const outletContext = useOutletContext<CheckoutGuardOutletContext>();
	const { checkoutSession, restaurant } = outletContext;
	const gtm = useGTMAnalytics();

	const [selectionState, setSelectionState] = useState<MandatorySelectionState>('selection_required');
	const [selectedItem, setSelectedItem] = useState<EcommerceMandatoryItemDTO | null>(null);

	const navigate = useNavigate();

	useEffect(() => {
		const { selectedItem: syncedItem, selectionState: syncedState } =
			syncMandatorySectionSelection(checkoutSession.items, sectionData.items);
		setSelectedItem(syncedItem);
		setSelectionState(syncedState);
	}, [checkoutSession.items, sectionData.items]);

	useEffect(() => {
		const ctaText = resolveLocalizedText(sectionData.cta, i18n.language);
		const items = sectionData.items.map(item => ({
			id: item.id,
			name: item.name,
			price: item.price,
			currency: item.currency || 'USD'
		}));

		analytics?.track(ECOMMERCE_SECTION_VIEWED, {
			title: sectionData.title,
			ctaText,
			numberOfItems: sectionData.items.length,
			items: JSON.stringify(items),
			checkoutPosition: sectionData.checkoutPosition,
			validationType: sectionData.validation?.type || 'open',
			reservationId: checkoutSession.id
		});
	}, [sectionData, i18n.language, checkoutSession.id]);

	const ctaLabel: Record<MandatorySelectionState, CheckoutCTALabel> = {
		selection_required: CheckoutCTALabel.SELECTION_REQUIRED,
		proceed: CheckoutCTALabel.ADDON_PROCEED
	};

	const { mutateAsync: updateCheckoutItems } = useUpdateCheckoutItems();

	const isUpdating = useIsMutating({
		mutationKey: [updateCheckoutItemsKey],
		exact: true
	});
	const isReleasing = useIsMutating({
		mutationKey: [releaseSessionKey],
		exact: true
	});

	const handleSelectItem = (id: string, variantId: string = id) => {
		if (selectedItem?.id === id && selectedItem?.variantId === variantId) {
			setSelectedItem(null);
			setSelectionState('selection_required');
			analytics?.track('Mandatory Item Deselected', {
				id,
				variantId,
				sectionTitle: sectionData.title,
				reservationId: checkoutSession.id
			});
		}
		else {
			const item = sectionData.items.find(i => i.variantId === variantId);
			const effectiveQuantityMode = item?.quantityMode ||
				(sectionData.validation.minQuantity === TDR.Ecommerce.QuantityMode.GROUP_SIZE ? TDR.Ecommerce.QuantityMode.GROUP_SIZE : TDR.Ecommerce.QuantityMode.SINGLE);

			const quantity = effectiveQuantityMode === TDR.Ecommerce.QuantityMode.GROUP_SIZE
				? (checkoutSession.guests || 1)
				: 1;

			setSelectedItem({ id, quantity, variantId });
			setSelectionState('proceed');
			analytics?.track('Mandatory Item Selected', {
				id,
				variantId,
				quantity,
				sectionTitle: sectionData.title,
				reservationId: checkoutSession.id
			});
		}
	};

	const handleProceedToNextStep = () => {
		if (!selectedItem) {
			setSelectionState('selection_required');
			return;
		}

		const currentStep = window.location.pathname.split('/').pop();

		const payload: UpdateCheckoutItemsInput = {
			checkoutId: checkoutSession.id,
			items: [selectedItem] as any,
			checkoutStep: currentStep
		};

		const selectedItemData = sectionData.items.find(item => item.id === selectedItem.id);

		analytics?.track(CTA_CLICKED_EVENT, {
			purpose: 'ecommerce proceed',
			type: 'mandatory',
			title: sectionData.title,
			numberOfItemsSelected: 1,
			items: JSON.stringify([{
				id: selectedItem.id,
				name: selectedItemData?.name || '',
				price: selectedItemData?.price || 0,
				quantity: selectedItem.quantity,
				currency: selectedItemData?.currency || 'USD'
			}]),
			validationType: sectionData.validation?.type || 'open',
			reservationId: checkoutSession.id
		});

		gtm.addToCart({
			checkoutId: checkoutSession.id,
			guests: checkoutSession.guests,
			items: [{
				id: selectedItem.id,
				name: selectedItemData?.name || '',
				price: selectedItemData?.price || 0,
				quantity: selectedItem.quantity
			}]
		});

		const sectionItemIds = new Set(sectionData.items.map(item => item.id));
		updateCheckoutState({
			addOnItemsDTO: mergeAddOnItemsDTO(checkoutState.addOnItemsDTO || [], [selectedItem], sectionItemIds)
		});
		updateCheckoutItems({ input: payload })
			.then(() => {
				const nextStep = getNextCheckoutStep();
				navigate(`../${nextStep}`);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const getNextCheckoutStep = () => {
		const pathname = window.location.pathname;
		const currentStep = pathname.split('/').pop();


		const currentNavItem = navConfig[currentStep];
		if (currentNavItem && currentNavItem.next) {
			return currentNavItem.next;
		}

		const orderedSteps = Object.values(navConfig).sort((a, b) => a.order - b.order);
		const currentIndex = orderedSteps.findIndex(step => step.path === currentStep);
		const nextStep = orderedSteps[currentIndex + 1];


		return nextStep?.path || 'policies';
	};

	const getNoticeText = () => {
		const noticeText = (sectionData as any).noticeText;
		const cta = sectionData.cta;
		return resolveLocalizedText(noticeText || cta, i18n.language);
	};

	const getButtonText = () => {
		// Check if we should show skip label (no item selected)
		if (selectionState === 'selection_required' && (sectionData as any).skipCtaLabel) {
			return resolveLocalizedText((sectionData as any).skipCtaLabel, i18n.language);
		}

		// Check if we should show normal CTA label (item selected)
		if (selectionState === 'proceed' && (sectionData as any).ctaLabel) {
			return resolveLocalizedText((sectionData as any).ctaLabel, i18n.language);
		}

		// Fallback to section CTA or default labels
		if (sectionData.cta) {
			return resolveLocalizedText(sectionData.cta, i18n.language);
		}

		return getCheckoutCTALabel(ctaLabel[selectionState], t);
	};

	return (
		<>
			<div className="flex flex-col flex-1 overflow-y-auto">
				<div className="flex flex-col gap-4 p-4 pt-40 sm:pt-4">
					<Notice text={getNoticeText()} />

					{sectionData.items.map((item) => (
						<SelectableEcommerceItem
							key={item.id}
							product={{
								id: item.id,
								title: item.name,
								description: item.description || '',
								price: {
									amount: item.price,
									unit: item.currency || 'USD'
								},
								variantId: item.variantId,
								imageUrl: item.imageUrl || '',
								tag: item.tag || '',
								unit: item.unit || 'unit',
								isSignatureItem: item.isSignatureItem || false
							}}
							restaurant={restaurant}
							onSelect={handleSelectItem}
							isSelected={selectedItem?.id === item.id}
						/>
					))}
				</div>
			</div>

			<div className="p-4 border-t border-gray-700">
				<CTAButton
					onClick={handleProceedToNextStep}
					buttonText={getButtonText()}
					loading={!!isUpdating}
					disabled={!!isUpdating || !!isReleasing || selectionState === 'selection_required'}
				/>
			</div>
		</>
	);
};